import { useEffect, useRef } from 'react'

import type { GameInfo } from '../api/client'
import { PixiSlot } from '../game/PixiSlot'
import type { ResolvedTheme } from '../theme'
import { frameShellStyle, OuterBoardFrame } from './OuterBoardFrame'

interface Props {
  /** The grid and symbols come from here — the lobby may hold several ways games. */
  game: GameInfo
  /** Styling of this game. A different theme rebuilds the board. */
  theme: ResolvedTheme
  /** Called once, when the board is ready to accept commands. */
  onReady: (slot: PixiSlot) => void
}

/**
 * The React → Pixi bridge.
 *
 * The component's only responsibility is to mount the canvas and hand
 * control outwards. There are deliberately no props here that affect the
 * contents of the board: any re-render during a spin has no chance
 * whatsoever of touching the game loop.
 */
export function ReelsCanvas({ game, theme, onReady }: Props): React.JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const slot = new PixiSlot()
    let disposed = false

    void slot.init(host, game, theme).then(() => {
      // StrictMode in dev mounts the effect twice. Without this check the
      // second instance would append another canvas on top of the first.
      if (disposed) {
        slot.destroy()
        return
      }
      onReady(slot)
    })

    return () => {
      disposed = true
      slot.destroy()
    }
  }, [game, theme, onReady])

  // See TumbleCanvas: a theme with its own frame art drops the stock panel.
  const board = (
    <div
      className={`board${theme.stage.panel ? '' : ' board--bare'}`}
      style={panelFrameStyle(theme)}
      ref={hostRef}
    />
  )
  if (!theme.board.frameImage || !theme.board.frameInset) return board
  return <div className="board-frame-shell" style={frameShellStyle(theme)}>{board}<OuterBoardFrame theme={theme} /></div>
}

const hex = (c: number): string => `#${c.toString(16).padStart(6, '0')}`

/**
 * Theme-coloured panel: the border ring is painted by a second gradient
 * clipped to the border box, so the frame can burn in fire colours while
 * the fill behind the canvas stays dark. Stock look when the theme is
 * silent.
 */
export function panelFrameStyle(theme: ResolvedTheme): React.CSSProperties | undefined {
  const frame = theme.stage.panelFrame
  if (!frame || !theme.stage.panel) return undefined
  const border = frame.border.map(hex).join(', ')
  const fill = (frame.background ?? [0x1c2230, 0x141922]).map(hex).join(', ')
  return {
    border: '3px solid transparent',
    background: `linear-gradient(180deg, ${fill}) padding-box, linear-gradient(180deg, ${border}) border-box`,
    boxShadow: `0 20px 50px rgba(0, 0, 0, 0.45), 0 0 26px ${hex(frame.border[0] ?? 0xff9a1f)}44`,
  }
}
